import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { BrandStatus } from "@/api/brand";
import { api } from "@/api/client";
import { isOrgSetupFactsKey, keys } from "@/api/queryKeys";
import type { Intent, SessionSummary } from "@/api/types";

export function useSessions() {
  return useQuery({
    queryKey: keys.sessions,
    queryFn: async () => (await api.get<SessionSummary[]>("/sessions")).data,
  });
}

export function useSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: keys.session(sessionId ?? ""),
    queryFn: async () =>
      (await api.get<SessionSummary>(`/sessions/${sessionId}`)).data,
    enabled: Boolean(sessionId),
    // Decision cards render from this row. The session bus pushes
    // ``session.updated`` to the owner and collaborators, but the bus
    // is in-process with no replay and other viewers (an admin looking
    // in) are not on it, so a lost or never-sent event left one person
    // staring at a card the others never saw. Polling while the tab is
    // visible is the safety net; the bus keeps it feeling instant.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

/** Other participants' unsent drafts on a session — powers the persistent
 *  typing/draft presence for a viewer that joins or reloads (live typing
 *  rides the session.typing SSE; this reconciles idle/persisted drafts and
 *  expires cleared ones). */
export type SessionDraftView = {
  user_id: string;
  name: string;
  chars: number;
  draft: string;
  updated_at: string;
};

export function useSessionDrafts(sessionId: string | undefined) {
  return useQuery({
    queryKey: ["session", sessionId ?? "", "drafts"],
    queryFn: async () =>
      (await api.get<SessionDraftView[]>(`/sessions/${sessionId}/drafts`)).data,
    enabled: Boolean(sessionId),
    refetchInterval: 20_000,
  });
}

// One of the session's-org ACTIVE prompt fragments, as returned by the
// session-scoped read endpoint (gated by session ownership). The
// in-session selector lists these and PATCHes the chosen subset.
export type AvailablePromptFragment = {
  /** The selection ref: an org fragment id or ``platform:<slug>``. */
  id: string;
  source?: "org" | "platform";
  kind: "style" | "base_kb" | "org_agents";
  intent: string | null;
  /** Target session types of a style; ``null`` = all. */
  intents?: string[] | null;
  /** Org title, or the English title of a Repoworks style (show the
   *  localised name by ``slug``). */
  title: string;
  /** Repoworks styles only. */
  slug?: string | null;
  category?: string | null;
  version?: number;
  /** Rendered layer length (``### Style:`` header + body). */
  chars?: number;
  /** Position in the server's precedence order among the listed styles
   *  (platform by slug, then org by title); null for other kinds. */
  layer_rank?: number | null;
  env_managed?: boolean;
  /** An org copy's source Repoworks style. */
  derived_from_slug?: string | null;
  /** A Repoworks style no longer offered (listed while still selected). */
  retired?: boolean;
  applies_by_default: boolean;
  /** Server-computed: the session uses it (its stored selection, or the
   *  org defaults for a legacy session that has none stored). */
  selected: boolean;
  /** Server-computed: it reaches the agent in this session's type. A
   *  style for another intent is ``false``; knowledge and organisation
   *  instructions are always ``true``. */
  applicable: boolean;
  /** Why ``applicable`` is false (empty when true): ``intent`` — a style
   *  for another session type; ``model`` — an operator fragment limited
   *  to other models than the one this session runs. */
  not_applicable_reasons?: NotApplicableReason[];
};

export type NotApplicableReason = "intent" | "model";

export const sessionPromptFragmentsKey = (sessionId: string) =>
  ["sessions", sessionId, "available-prompt-fragments"] as const;

export function useSessionPromptFragments(sessionId: string) {
  return useQuery({
    queryKey: sessionPromptFragmentsKey(sessionId),
    queryFn: async () =>
      (
        await api.get<AvailablePromptFragment[]>(
          `/sessions/${sessionId}/available-prompt-fragments`,
        )
      ).data,
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}

// One of the session's-org brand profiles, as returned by the
// session-scoped (participant-gated) read endpoint. The context panel's
// BrandProfileSelect lists these and PATCHes ``brand_profile_id``.
export type AvailableBrandProfile = {
  id: string;
  name: string;
  is_primary: boolean;
  /** FW-47: whether the profile carries the org's own branding. */
  brand_status?: BrandStatus;
};

export function useAvailableBrandProfiles(sessionId: string) {
  return useQuery({
    queryKey: ["session", sessionId, "available-brand-profiles"] as const,
    queryFn: async () =>
      (
        await api.get<AvailableBrandProfile[]>(
          `/sessions/${sessionId}/available-brand-profiles`,
        )
      ).data,
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Partial<
        Pick<
          SessionSummary,
          | "title"
          | "openai_account_id"
          | "repository_id"
          | "branch_name"
          | "model"
          | "model_reasoning_effort"
          | "model_reasoning_summary"
          | "model_verbosity"
          | "service_tier"
          | "intent"
          | "mode"
          | "language"
        >
      > & {
        gitlab_project_id?: number | null;
        // Org the session belongs to. Sent explicitly by the create
        // form so the org shown there is the org the session lands in
        // — omitting it lets the backend resolve its own fallback,
        // which could disagree with what the UI displayed.
        org_slug?: string;
        // Agent provider slug ("codex" / "claude_code"). Optional —
        // omit to fall back to the registry's default. Stamped on
        // the session row at creation and immutable afterwards.
        provider?: string;
        // Custom LLM provider (FW-19): ``model`` is then that provider's
        // model slug and ``provider`` follows from its API format.
        llm_provider_id?: string;
        // Branch-to-iterate: seed the new session from a parent. The
        // service inherits the parent's repo/branch/intent/mode/language
        // and context_project_refs; the document_id is stashed in the
        // new session's metadata so the composer can pre-attach it as
        // a reference.
        derived_from_session_id?: string | null;
        derived_from_document_id?: string | null;
        // Primary document for ``*_modify`` intents. Backend rejects
        // it on every other intent (422 primary_only_for_modify),
        // checks the format matches (docx vs pptx), and verifies the
        // caller can view the row.
        primary_document_id?: string | null;
        // Brand profile to render with. Omit to let the backend pick
        // (the iterated document's profile, else the parent session's,
        // else the org primary).
        brand_profile_id?: string | null;
        // Anthropic extended-thinking effort (claude_code only). Maps
        // to ``--effort <level>`` on Claude Code's CLI. Omit to let
        // the model decide.
        extended_thinking_level?:
          | "low"
          | "medium"
          | "high"
          | "xhigh"
          | "max"
          | null;
      },
    ) => (await api.post<SessionSummary>("/sessions", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.sessions });
      // A first session ticks the org's setup step.
      qc.invalidateQueries({ predicate: isOrgSetupFactsKey });
    },
  });
}

/** Context-project-ref payload shape for the update_session API.
 *
 * Phase H: backend accepts EITHER the legacy {gitlab_project_id}
 * OR the multi-source {code_source_id, project_id} pair. Both
 * include path_with_namespace + branch_name. The picker emits the
 * multi-source pair so GitLab + GitHub both work via the same
 * shape; legacy clients can keep sending the int.
 */
export type ContextProjectRefIn = {
  gitlab_project_id?: number | null;
  code_source_id?: string | null;
  project_id?: string | null;
  path_with_namespace: string;
  branch_name: string;
};

/** Recompute drift for one document on demand. Backend runs the
 *  same ``rev-list --count`` the scheduled checker does, but
 *  scoped + ignoring the freshness window — useful when the user
 *  just pushed a fix and wants to see drift update without waiting
 *  for the next retention tick. */
export function useRecheckDocumentDrift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await api.post(`/documents/${documentId}/drift/recheck`);
      return documentId;
    },
    onSuccess: () => {
      // Drift lives on every document-listing query; invalidate
      // broadly so the library page + per-session pane both pick
      // up the new ``drift_status``.
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
    },
  });
}


/** Force-refresh the session's ``linked_mrs`` cache against GitLab.
 *  The backend endpoint clears the TTL marker and schedules a
 *  background fetch; we just invalidate the session query so the
 *  SPA picks up the new value once the refresh lands. */
export function useRefreshLinkedMrs(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/sessions/${sessionId}/linked-mrs/refresh`);
    },
    onSuccess: () => {
      // The background task takes a beat; invalidate now and again
      // shortly after so the SPA repaints when the cache lands.
      qc.invalidateQueries({ queryKey: keys.session(sessionId) });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: keys.session(sessionId) });
      }, 1500);
    },
  });
}


export function useUpdateSession(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Partial<
        Pick<
          SessionSummary,
          | "title"
          | "branch_name"
          | "repository_id"
          | "openai_account_id"
          | "model"
          | "model_reasoning_effort"
          | "model_reasoning_summary"
          | "model_verbosity"
          | "mode"
          | "language"
        >
      > & {
        gitlab_project_id?: number | null;
        clear_repository?: boolean;
        clear_mode?: boolean;
        clear_language?: boolean;
        clear_service_tier?: boolean;
        // Switch the agent runtime mid-session. The backend drops
        // resume state + resets the model so the new provider's
        // default takes over on the next turn.
        provider?: string | null;
        // Phase H: ContextProjectRef accepts both legacy
        // (gitlab_project_id) and multi-source (code_source_id +
        // project_id) shapes. The frontend picker emits the
        // multi-source pair; the backend resolves through
        // ensure_repository_from_code_source so GitLab + GitHub
        // both round-trip cleanly.
        add_context_ref?: ContextProjectRefIn | null;
        remove_context_ref?: ContextProjectRefIn | null;
        promote_context_ref?: ContextProjectRefIn | null;
        // Remove an agent-materialised read-only reference repo by its slug.
        remove_secondary_ref?: string | null;
        // Multi-edit (phase 3): mark / unmark a context ref as
        // additionally editable. Add triggers a clone-on-link the
        // same way add_context_ref does so the next turn can push
        // without a 30-second stall.
        add_editable_ref?: ContextProjectRefIn | null;
        remove_editable_ref?: ContextProjectRefIn | null;
        // Full-replace map of ``{repo_uuid: branch_name}``. SPA
        // sends the post-mutation snapshot; ``null`` leaves
        // overrides untouched.
        branch_overrides?: Record<string, string> | null;
        reset_auto_title?: boolean;
        mark_title_edited?: boolean;
        ticket_number?: string | null;
        clear_ticket?: boolean;
        primary_attachment_name?: string | null;
        clear_primary_attachment?: boolean;
        intent?: Intent;
        // Internal-Document references — list of UUID strings, capped
        // at 16 by the backend. ``null`` leaves the existing list
        // untouched; an empty list (or ``clear_internal_doc_refs``)
        // wipes it.
        internal_doc_refs?: string[] | null;
        clear_internal_doc_refs?: boolean;
        // Org prompt-fragment selection — list of UUID strings.
        // ``null`` leaves the existing selection untouched; an empty
        // list means "none"; ``clear_prompt_fragment_ids`` resets it to
        // the org's current defaults for the session's intent.
        prompt_fragment_ids?: string[] | null;
        clear_prompt_fragment_ids?: boolean;
        // Primary among the internal doc refs — the modify-flow
        // edits this Document. Server validates membership in
        // ``internal_doc_refs``; setting one not in the list is
        // ``primary_internal_doc_ref_not_in_refs``.
        primary_internal_doc_ref_id?: string | null;
        clear_primary_internal_doc_ref?: boolean;
        // Anthropic extended-thinking effort; see useCreateSession
        // for the semantics.
        extended_thinking_level?:
          | "low"
          | "medium"
          | "high"
          | "xhigh"
          | "max"
          | null;
        clear_extended_thinking?: boolean;
        // Runtime-mode picker: set or clear the per-session chat runtime mode.
        chat_runtime_mode?: string | null;
        clear_chat_runtime_mode?: boolean;
        // Switch the brand profile documents render with (validated
        // in-org server-side; 409 turn_in_progress during a turn).
        brand_profile_id?: string | null;
      },
    ) =>
      (await api.patch<SessionSummary>(`/sessions/${sessionId}`, payload)).data,
    onSuccess: (data, variables) => {
      qc.setQueryData(keys.session(sessionId), data);
      qc.invalidateQueries({ queryKey: keys.sessions });
      if (
        variables.prompt_fragment_ids != null ||
        variables.clear_prompt_fragment_ids ||
        variables.intent !== undefined
      ) {
        // ``selected`` / ``applicable`` on the available list are
        // server-computed; an intent switch can change both.
        qc.invalidateQueries({
          queryKey: sessionPromptFragmentsKey(sessionId),
        });
      }
      if (variables.brand_profile_id !== undefined) {
        // A brand switch appends a ``brand_profile_changed`` transcript
        // event and changes the theme variants on offer (variants are
        // per-profile tokens; that query has a 5-min staleTime).
        qc.invalidateQueries({ queryKey: keys.session(sessionId), exact: true });
        qc.invalidateQueries({ queryKey: keys.messages(sessionId) });
        qc.invalidateQueries({
          queryKey: ["docgen", "theme-variants", sessionId],
        });
      }
      // Workspace-tree refresh: PATCHes that touch context refs,
      // editable toggles, or the primary repo all change the set
      // of roots the right-panel tree should render. Without this
      // invalidation the tree stayed stuck at the pre-mutation
      // snapshot until the next tool_result event re-fetched it,
      // so toggling editable on a ref looked like nothing happened
      // until the user ran a turn.
      qc.invalidateQueries({
        queryKey: ["sessions", sessionId, "workspace", "roots"],
      });
    },
  });
}

/** Make several linked projects editable at once (the context panel's
 *  "Make all editable"). One result per project; the backend applies each
 *  the way the pencil does and says why any stayed read-only. */
export function useMakeRefsEditable(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      refs,
      secondaries,
    }: {
      refs: ContextProjectRefIn[];
      /** Read-only references by slug. */
      secondaries: string[];
    }) =>
      (
        await api.post<{
          results: import("@/features/sessions/editableRefResults").EditableRefResult[];
          session: SessionSummary;
        }>(`/sessions/${sessionId}/editable-refs`, { refs, secondaries })
      ).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.session(sessionId), data.session);
      qc.invalidateQueries({ queryKey: keys.sessions });
      // New editable projects are new workspace roots (see useUpdateSession).
      qc.invalidateQueries({
        queryKey: ["sessions", sessionId, "workspace", "roots"],
      });
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/sessions/${sessionId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions }),
  });
}

export function useUserSearch(q: string) {
  const debouncedQ = q; // caller should debounce before passing here
  return useQuery({
    queryKey: keys.userSearch(debouncedQ),
    queryFn: async () =>
      (
        await api.get<import("@/api/types").UserStub[]>(
          `/users/search?q=${encodeURIComponent(debouncedQ)}`,
        )
      ).data,
    enabled: debouncedQ.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Search for a user to add as a collaborator of ``sessionId``.
 *
 * Scoped to the session's own org (the caller already manages this one
 * session, so that search surface is safe) rather than ``useUserSearch``'s
 * ``/users/search``. For a ROOT-org session this still excludes plain
 * root co-membership from counting as "colleagues" unless the deploy
 * opts in via ``RW_ROOT_ORG_IS_TENANT`` (single-company installs) — see
 * the backend's ``root_org_is_tenant`` setting and ``CollaboratorsCard``. */
export function useCollaboratorCandidates(sessionId: string, q: string) {
  const debouncedQ = q; // caller should debounce before passing here
  return useQuery({
    queryKey: keys.collaboratorCandidates(sessionId, debouncedQ),
    queryFn: async () =>
      (
        await api.get<import("@/api/types").UserStub[]>(
          `/sessions/${sessionId}/collaborator-candidates?q=${encodeURIComponent(debouncedQ)}`,
        )
      ).data,
    enabled: sessionId.length > 0 && debouncedQ.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAddCollaborator(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (
        await api.post<{ ok: boolean }>(`/sessions/${sessionId}/collaborators`, {
          user_id: userId,
        })
      ).data,
    onSuccess: () => {
      // The endpoint answers {ok:true} — writing THAT into the session
      // cache blanked the open session view until the next refetch.
      qc.invalidateQueries({ queryKey: keys.session(sessionId) });
      qc.invalidateQueries({ queryKey: keys.sessions });
    },
  });
}

export function useRemoveCollaborator(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (collaboratorId: string) =>
      (
        await api.delete<{ ok: boolean }>(
          `/sessions/${sessionId}/collaborators/${collaboratorId}`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.session(sessionId) });
      qc.invalidateQueries({ queryKey: keys.sessions });
    },
  });
}

export function useArchivedSessions(enabled = true) {
  return useQuery({
    queryKey: ["sessions", "archived"],
    queryFn: async () =>
      (await api.get<SessionSummary[]>("/sessions/archived")).data,
    enabled,
  });
}

export function useArchiveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.post(`/sessions/${sessionId}/archive`);
    },
    onSuccess: (_data, sessionId) => {
      qc.invalidateQueries({ queryKey: keys.sessions });
      qc.invalidateQueries({ queryKey: keys.session(sessionId) });
      qc.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
  });
}

export function useUnarchiveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) =>
      (await api.post<SessionSummary>(`/sessions/${sessionId}/unarchive`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: keys.sessions });
      qc.invalidateQueries({ queryKey: keys.session(data.id) });
      qc.invalidateQueries({ queryKey: ["sessions", "archived"] });
    },
  });
}
